import { useCallback, useEffect, useState } from 'react';
import { InputNumber, message, Spin } from 'antd';
import debounce from "lodash/debounce";
import moment from 'moment/moment';
import ClassTypes from '../enums/class_types';
import { fetchAttendees } from '../apis/eventsApi';
import { checkAttendee } from '../apis/sessionsApi';
import { patchUser } from '../apis/usersApi';


const Attendees = ({event}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [attendees, setAttendees] = useState([]);
    const [loadingIds, setLoadingIds] = useState([]);

    const debouncedPatch = useCallback(
        debounce(async (user_id, field, value) => {
            setLoadingIds(prev => [...prev, user_id]);
            try {
                const response = await patchUser(user_id, { [field]: value });
                if (response.success) {
                    message.success('Usuario actualizado');
                    setAttendees(prev =>
                        prev.map(attendee =>
                            attendee.user_id === user_id ? { ...attendee, [field]: value } : attendee
                        )
                    );
                } else {
                    message.error('Error actualizando usuario');
                }
            } catch (err) {
                console.error('Error updating user:', err);
                message.error('Error desconocido, contacta a soporte');
            } finally {
                setLoadingIds(prev => prev.filter(id => id !== user_id));
            }
        }
        , 500) // waits 500ms after the last change, to avoid too many requests on quick changes
    ,[]);

    useEffect(() => {
        if (!event) return;
        const loadAttendees = async () => {
        try {
            setAttendees(await fetchAttendees(event));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
        };
        loadAttendees();
    }, [event]);

    const handleCheckAttendee = async (session_id, checked) => {
        try {
          const result = await checkAttendee(session_id, checked);
          if (!result.success) {
            message.error("Error al verificar la asistencia, contacta a soporte.");
          }
          setAttendees(prev =>
            prev.map(row =>
              row.id === session_id ? { ...row, attended: checked } : row
            )
          );
        } catch (error) {
          console.error("Error updating attendance:", error);
        }
    };

    if (loading) return <Spin size="large" tip="Cargando..." />
    if (error) return <p>Error: {error}</p>;

    const isKangoo =
        event?.class_type_id === ClassTypes.FLYR ||
        event?.class_type_id  === ClassTypes.FLYR_KIDS;

    const handleEdit = async (user_id, field, value) => {
        if (value !== undefined && value !== null){
            debouncedPatch(user_id, field, value);
        }
    };

    return (
        <>
            <h2>Asistentes:</h2>
            <h3 className="text-muted"> {event.name}</h3>
            <h4 className="text-muted">
            <span className="font-weight-bold">Día:</span> {moment.utc(event.start_date).local().format('dddd D [de] MMMM')}
            </h4>
            <h4 className="text-muted">
            <span className="font-weight-bold">Hora:</span> {moment.utc(event.start_date).local().format('HH:mm')} - {moment.utc(event.end_date).local().format('HH:mm')}
            </h4>
            <div className="table-responsive">
                <table className="w-100 table">
                    <thead className="thead-light">
                    <tr>
                        <th scope="col">Id</th>
                        <th scope="col">Nombre</th>
                        <th scope="col">Es cortesía</th>
                        <th scope="col">Asistió</th>
                        {/* TODO assign kangoo an show this column isKangoo && <th scope="col">Kangoo</th> */}
                        {isKangoo && <th scope="col">Peso</th>}
                        {isKangoo && <th scope="col">Talla</th>}
                        <th scope="col">Patologías</th>
                    </tr>
                    </thead>
                    <tbody>
                    {attendees.map((attendee) => (
                        <tr key={attendee.user_id}>
                        <td>
                            <div>
                            {attendee.user_id}
                            </div>
                        </td>
                        <td>
                            <a
                            className="client-icon"
                            href={`/users/${attendee.user_id}`}
                            >
                            <div style={{ maxHeight: "3rem", overflow: "hidden" }}>
                                {`${attendee.name} ${attendee.surname_1 || ""} ${attendee.surname_2 || ""}`}
                            </div>
                            </a>
                        </td>
                        <td>
                            <div style={{ maxHeight: "3rem", overflow: "hidden" }}>
                                {attendee.is_courtesy ? "Si" : "No"}
                            </div>
                        </td>
                        <td>
                            <div style={{ maxHeight: "3rem", overflow: "hidden" }}>
                            <input
                                className="form-check-input position-relative"
                                type="checkbox"
                                name="attended"
                                id={`attended_${attendee.id}`}
                                checked={!!attendee.attended}
                                onChange={e => handleCheckAttendee(attendee.id, e.target.checked)}
                                required
                            />
                            </div>
                        </td>
                        {/* isKangoo && ( TODO assign kangoo an show this column
                            <>
                            <td>
                                <div style={{ maxHeight: "3rem", overflow: "hidden" }}>
                                {attendee.kangoo_id
                                    ? attendee.kangoo?.SKU
                                    : ""}
                                </div>
                            </td>
                            */}
                        { isKangoo && (
                            <>
                                <td>
                                    <div style={{ maxHeight: "3rem", overflow: "hidden" }}>
                                        { attendee.weight_in_kg ?? "" }
                                    </div>
                                </td>
                                <td>
                                    <div style={{ maxHeight: "3rem", overflow: "hidden" }}>
                                        <InputNumber
                                            min={30}
                                            max={50}
                                            value={attendee.shoe_size || undefined}
                                            onChange={value =>
                                                handleEdit(attendee.user_id, "shoe_size", value)
                                            }
                                            disabled={loadingIds.includes(attendee.user_id)}
                                            style={{
                                                backgroundColor: loadingIds.includes(attendee.user_id)
                                                ? "#eee"
                                                : "white",
                                            }}
                                        />
                                    </div>
                                </td>
                            </>
                        )}
                        <td>
                            {attendee.pathology ? (
                            <p
                                className="text-truncate"
                                style={{ maxWidth: 150 }}
                                title={attendee.pathology}
                            >
                                {attendee.pathology}
                            </p>
                            ) : (
                            <p className="text-muted">-</p>
                            )}
                        </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default Attendees;